import React, { useState, useEffect, useRef, Fragment } from "react";
import { MinimalContainer } from "../../styles/Theme";
import styled from "styled-components";
import Link from "../Link";
import CompanyDropDown from "../crm/CompanyDropDown";
import CompanyBranchDropDown from "../crm/CompanyBranchDropDown";
import TechniqueInput from "../testing/TechniqueInput";
import TestnameInput from "../testing/TestnameInput";
import MatrixInput from "../testing/MatrixInput";
import Button from "../Button";
import Input from "../Input";
import ResultStateInput from "./inputs/ResultStateInput";
import ResultPresenceInput from "./inputs/ResultPresenceInput";
import ExceededLimitsInput from "./inputs/ExceededLimitsInput";
import RequiresNotificationAuthorisationInput from "./inputs/ExceededLimitsInput";
import ResultDetectedInput from "./inputs/ResultDetectedInput";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import { DateRangePicker } from "react-dates";
import RadioButtons from "../RadioButtons";
import moment from "moment";
import TestingCategorySelect from "../sample_reception/TestingCategorySelect";
import DataFieldFilter from "../sample_reporting/DataFieldFilter";
import QualifierInput from "../common/form/QualifierInput";
import Checkbox from "../common/form/Checkbox";
import { renderMonthElement } from "../common/CalendarUtils";
import SearchTemplateManager from "../common/SearchTemplateManager";

const Container = styled(MinimalContainer)`
  padding: 20px;
  max-width: 1920px;
  margin: 0 auto;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -15px;
`;

const Column = styled.div`
  flex-grow: 1;
  min-width: 350px;
  padding: 0 15px;
  margin-bottom: 10px;
`;

const FullColumn = styled.div`
  width: 100%;
  padding: 0 15px;
`;

export default function SampleTestSearch({
  laboratory_company_id,
  onSearch,
  initialSampleId,
  initialAliquotId,
  clearDates,
  clientView = false,
  companyId,
  fixedSearchParameters = {},
}) {
  const dataFieldFilter = useRef(null);
  const [sampleId, setSampleId] = useState(initialSampleId);
  const [aliquotId, setAliquotId] = useState(initialAliquotId);
  const [techniqueIds, setTechniqueIds] = useState([]);
  const [matrixIds, setMatrixIds] = useState(fixedSearchParameters.matrix_ids || [])
  const [testnameIds, setTestnameIds] = useState([]);
  const [productName, setProductName] = useState("");
  const [clientCompanyId, setClientCompanyId] = useState(companyId);
  const [clientBranchId, setClientBranchId] = useState(null);
  const [laboratoryBranchId, setLaboratoryBranchId] = useState(null);
  const [resultState, setResultState] = useState("all");
  const [resultPresence, setResultPresence] = useState("all");
  const [exceededLimits, setExceededLimits] = useState(false);
  const [requiresNotificationAuthorisation, setRequiresNotificationAuthorisation] = useState(
    false
  );
  const [awaitingNotification, setAwaitingNotification] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState(fixedSearchParameters.date_filter || "registered");
  const [startDate, setStartDate] = useState(
    clearDates ? null :   
    fixedSearchParameters.start_date ? moment(fixedSearchParameters.start_date) : moment().subtract(5, "days")
  );
  const [endDate, setEndDate] = useState(clearDates ? null : moment());
  const [focusedInput, setFocusedInput] = useState(null);
  const [found, setFound] = useState(false);
  const [allSampleResultsPresent, setAllSampleResultsPresent] = useState(fixedSearchParameters.all_sample_results_present || false);
  const [exceedAnalysisTime, setExceedAnalysisTime] = useState(false);
  const [testingCategory, setTestingCategory] = useState(fixedSearchParameters.testing_category_id);
  const [qualifierIds, setQualifierIds] = useState(fixedSearchParameters.qualifier_ids || [])
  const [excludeCompany, setExcludeCompany] = useState(false);
  const [excludeMatrix, setExcludeMatrix] = useState(fixedSearchParameters.exclude_matrix || false);
  const [excludeQualifier, setExcludeQualifier] = useState(fixedSearchParameters.exclude_qualifier || false);
  const [jobId, setJobId] = useState(fixedSearchParameters.job_id);
  const [
    analysis_started_more_than_24_hrs_since_time_sampled,
    set_analysis_started_more_than_24_hrs_since_time_sampled,
  ] = useState(fixedSearchParameters.analysis_started_more_than_24_hrs_since_time_sampled || false)
  const [
    analysis_started_less_than_24_hrs_since_time_sampled,
    set_analysis_started_less_than_24_hrs_since_time_sampled,
  ] = useState(fixedSearchParameters.analysis_started_less_than_24_hrs_since_time_sampled || false)

  const formKey = "sample_testing/sample_test_search";

  const getSearchParams = () => ({
    sample_id: sampleId,
    technique_ids: techniqueIds,
    matrix_ids: matrixIds,
    testname_ids: testnameIds,
    client_company_id: clientCompanyId,
    company_branch_ids: clientBranchId ? [clientBranchId] : null,
    laboratory_sample_branch_id: laboratoryBranchId,
    result_state: resultState,
    result_presence: resultPresence,
    requires_notification_authorisation: requiresNotificationAuthorisation,
    awaiting_notification: awaitingNotification,
    exceed: exceededLimits,
    date_filter: dateFilter,
    start_date: startDate ? startDate.format("YYYY-MM-DD") : null,
    end_date: endDate ? endDate.format("YYYY-MM-DD") : null,
    testing_category_id: testingCategory,
    found,
    qualifier_ids: qualifierIds,
    exclude_client_company: excludeCompany,
    exclude_matrix: excludeMatrix,
    exclude_qualifier: excludeQualifier,
    data_fields: dataFieldFilter && dataFieldFilter.current ? dataFieldFilter.current.queryParams() : [],
    all_sample_results_present: allSampleResultsPresent,
    analysis_started_more_than_24_hrs_since_time_sampled,
    analysis_started_less_than_24_hrs_since_time_sampled,
    exceed_analysis_time: exceedAnalysisTime,
    job_id: jobId,
    product_name: productName,
  });

  const applyTemplate = (tpl) => {
    if ("sample_id" in tpl) setSampleId(tpl.sample_id || "");
    if ("technique_ids" in tpl) setTechniqueIds(Array.isArray(tpl.technique_ids) ? tpl.technique_ids : []);
    if ("matrix_ids" in tpl) setMatrixIds(Array.isArray(tpl.matrix_ids) ? tpl.matrix_ids : []);
    if ("testname_ids" in tpl) setTestnameIds(Array.isArray(tpl.testname_ids) ? tpl.testname_ids : []);
    if ("client_company_id" in tpl) setClientCompanyId(tpl.client_company_id || null);
    if ("company_branch_ids" in tpl) setClientBranchId(Array.isArray(tpl.company_branch_ids) ? tpl.company_branch_ids[0] : null);
    if ("laboratory_sample_branch_id" in tpl) setLaboratoryBranchId(tpl.laboratory_sample_branch_id || null);
    if ("result_state" in tpl) setResultState(tpl.result_state || "all");
    if ("result_presence" in tpl) setResultPresence(tpl.result_presence || "all");
    if ("requires_notification_authorisation" in tpl) setRequiresNotificationAuthorisation(!!tpl.requires_notification_authorisation);
    if ("awaiting_notification" in tpl) setAwaitingNotification(!!tpl.awaiting_notification);
    if ("exceed" in tpl) setExceededLimits(!!tpl.exceed);
    if ("date_filter" in tpl) setDateFilter(tpl.date_filter || "registered");
    if ("start_date" in tpl) setStartDate(tpl.start_date ? moment(tpl.start_date) : null);
    if ("end_date" in tpl) setEndDate(tpl.end_date ? moment(tpl.end_date) : null);
    if ("testing_category_id" in tpl) setTestingCategory(tpl.testing_category_id || null);
    if ("found" in tpl) setFound(!!tpl.found);
    if ("qualifier_ids" in tpl) setQualifierIds(Array.isArray(tpl.qualifier_ids) ? tpl.qualifier_ids : []);
    if ("exclude_client_company" in tpl) setExcludeCompany(!!tpl.exclude_client_company);
    if ("exclude_matrix" in tpl) setExcludeMatrix(!!tpl.exclude_matrix);
    if ("exclude_qualifier" in tpl) setExcludeQualifier(!!tpl.exclude_qualifier);
    if ("all_sample_results_present" in tpl) setAllSampleResultsPresent(!!tpl.all_sample_results_present);
    if ("analysis_started_more_than_24_hrs_since_time_sampled" in tpl)
      set_analysis_started_more_than_24_hrs_since_time_sampled(!!tpl.analysis_started_more_than_24_hrs_since_time_sampled);
    if ("analysis_started_less_than_24_hrs_since_time_sampled" in tpl)
      set_analysis_started_less_than_24_hrs_since_time_sampled(!!tpl.analysis_started_less_than_24_hrs_since_time_sampled);
    if ("exceed_analysis_time" in tpl) setExceedAnalysisTime(!!tpl.exceed_analysis_time);
    if ("job_id" in tpl) setJobId(tpl.job_id || null);
    if ("product_name" in tpl) setProductName(tpl.product_name || "");
  };

  const valid = () => {
    return (startDate && endDate) || sampleId || aliquotId || jobId;
  };

  const onClick = () => {
    setLoading(true);
    valid() &&
      onSearch &&
      onSearch(
        {
          sample_id: sampleId,
          technique_ids: techniqueIds,
          matrix_ids: matrixIds,
          testname_ids: testnameIds,
          client_company_id: clientCompanyId,
          company_branch_ids: clientBranchId ? [clientBranchId] : null,
          laboratory_sample_branch_id: laboratoryBranchId,
          result_state: resultState,
          result_presence: resultPresence,
          requires_notification_authorisation: requiresNotificationAuthorisation,
          awaiting_notification: awaitingNotification,
          exceed: exceededLimits,
          date_filter: dateFilter,
          start_date: startDate ? startDate.format("YYYY-MM-DD") : null,
          end_date: endDate ? endDate.format("YYYY-MM-DD") : null,
          testing_category_id: testingCategory,
          found,
          qualifier_ids: qualifierIds,
          exclude_client_company: excludeCompany,
          exclude_matrix: excludeMatrix,
          exclude_qualifier: excludeQualifier,
          data_fields:
            dataFieldFilter && dataFieldFilter.current
              ? dataFieldFilter.current.queryParams()
              : [],
          all_sample_results_present: allSampleResultsPresent,
          analysis_started_more_than_24_hrs_since_time_sampled,
          analysis_started_less_than_24_hrs_since_time_sampled,
          exceed_analysis_time: exceedAnalysisTime,
          job_id: jobId,
          product_name: productName,
        },
        () => {
          setLoading(false);
        }
      );
  };

  useEffect(() => {
    if (initialAliquotId) {
      onClick();
    }
  }, []);

  return (
    <Fragment>
      <Container>
        <Row>
          <Column>
            <label>{I18n.t("testing_category")}</label>
            <TestingCategorySelect
              value={testingCategory}
              onChange={(o) => setTestingCategory(o.value)}
            />
          </Column>
          <Column>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="form-control input-xs"
              style={{ height: 27 }}
            >
              <option value="registered">{I18n.t("sample_registered_between")}</option>
              <option value="result_entered">{I18n.t("result_entered_between")}</option>
              <option value="due_date">{I18n.t("sample_due_date_between")}</option>
              {!clientView && (
                <option value="sample_test_scheduled">
                  {I18n.t("sample_test_scheduled_between")}
                </option>
              )}
            </select>
            <DateRangePicker
              startDateId="start_date"
              startDate={startDate}
              endDateId="end_date"
              endDate={endDate}
              numberOfMonths={1}
              onDatesChange={({ startDate, endDate }) => {
                setStartDate(startDate);
                setEndDate(endDate);
              }}
              focusedInput={focusedInput}
              onFocusChange={(focusedInput) => setFocusedInput(focusedInput)}
              showClearDates={true}
              isOutsideRange={() => false}
              hideKeyboardShortcutsPanel={true}
              displayFormat="DD MMM YYYY"
              minimumNights={0}
              renderMonthElement={renderMonthElement}
            />
          </Column>
          <Column>
            <label>{I18n.t("sample_id")}</label>
            <Input
              className="form-control"
              value={sampleId}
              onChange={(e) => setSampleId(e.target.value)}
            />
          </Column>
          {!clientView && (
            <Column>
              <label>{I18n.t("aliquot_id")}</label>
              <Input
                className="form-control"
                value={aliquotId}
                onChange={(e) => setAliquotId(e.target.value)}
                onPressEnter={onClick}
              />
            </Column>
          )}
          <Column>
            <label>{I18n.t("technique")}</label>
            <TechniqueInput
              multiple
              value={techniqueIds}
              returnIds
              onChange={(ids) => {
                setTechniqueIds(Array.isArray(ids) ? ids : (ids ? [ids] : []));
              }}
              defaultStyle
            />
          </Column>
          <Column>
            <label>{I18n.t("product_name")}</label>
            <Input
              focus
              className="form-control"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
            />
          </Column>
          <Column>
            <label style={{ width: "100%" }}>
              {I18n.t("matrix")}
              <span className="pull-right">
                <input
                  type="checkbox"
                  checked={excludeMatrix}
                  style={{ margin: 0 }}
                  onChange={(e) => setExcludeMatrix(e.target.checked)}
                />
                &nbsp; Exclude?
              </span>
            </label>

            <MatrixInput
              multiple
              value={matrixIds}
              onChange={(options) => {
                if (Array.isArray(options)) {
                  setMatrixIds(options.map((o) => o.value));
                } else {
                  setMatrixIds(options ? [options.value] : []);
                }
              }}
              defaultIds={fixedSearchParameters.matrix_ids}
              defaultStyle
            />
          </Column>
          <Column>
            <label>{I18n.t("testname")}</label>
            <TestnameInput
              multiple
              value={testnameIds}
              returnIds
              onChange={(ids) => {
                setTestnameIds(Array.isArray(ids) ? ids : (ids ? [ids] : []));
              }}
              defaultStyle
              hideInternal={clientView}
            />
          </Column>
          {!clientView && (
            <Column>
              <label style={{ width: "100%" }}>
                {I18n.t("client_company")}
                <span className="pull-right">
                  <input
                    type="checkbox"
                    value={excludeCompany}
                    style={{ margin: 0 }}
                    onChange={(e) => setExcludeCompany(e.target.checked)}
                  />
                  &nbsp; Exclude?
                </span>
              </label>
              <CompanyDropDown
                value={clientCompanyId}
                onChange={(company_id) => setClientCompanyId(company_id)}
              />
            </Column>
          )}
          <Column>
            <label style={{ width: "100%" }}>
              {I18n.t("qualifier")}
              <span className="pull-right">
                <input
                  type="checkbox"
                  checked={excludeQualifier}
                  style={{ margin: 0 }}
                  onChange={(e) => setExcludeQualifier(e.target.checked)}
                />
                &nbsp; Exclude?
              </span>
            </label>
            <br />
            <QualifierInput value={qualifierIds} onChange={(ids) => setQualifierIds(ids)} />
          </Column>
          <Column>
            <label>{I18n.t("client_branch")}</label>
            <CompanyBranchDropDown
              key={clientCompanyId}
              company_id={clientCompanyId}
              onBranchChange={(branch) => setClientBranchId(branch ? branch.id : null)}
              value={clientBranchId}
              includeBlank={clientView ? false : true}
              blankLabel={"All Branches"}
              permission_code={"view_results"}
            />
          </Column>
          <Column>
            <label>{I18n.t("result_present")}</label>
            <br />
            <ResultPresenceInput value={resultPresence} onChange={setResultPresence} />
          </Column>
          <Column>
            <label>{I18n.t("reviewed?")}</label>
            <br />
            <ResultStateInput value={resultState} onChange={setResultState} />
          </Column>
        </Row>
        <Row>
          <Column>
            <label style={{ marginRight: 20 }}>
              <ResultDetectedInput value={found} onChange={setFound} />
              &nbsp;&nbsp;
              {I18n.t("result_detected")}
            </label>
            <label style={{ marginRight: 20 }}>
              <ExceededLimitsInput value={exceededLimits} onChange={setExceededLimits} />
              &nbsp;&nbsp;
              {I18n.t("exceeded_limits")}
              &nbsp;
              <span
                className="badge"
                data-toggle="tooltip"
                data-placement="top"
                title="For Pesticide samples, only EU MRL limits are used to flag failures on this page"
              >
                <i className="fa fa-info" />
              </span>
            </label>
            {!clientView && (
              <label style={{ marginRight: 20 }}>
                <RequiresNotificationAuthorisationInput
                  value={requiresNotificationAuthorisation}
                  onChange={setRequiresNotificationAuthorisation}
                />
                &nbsp; {I18n.t("requires_notification_authorisation")}
              </label>
            )}
            {!clientView && (
              <label style={{ marginRight: 20 }}>
                <RequiresNotificationAuthorisationInput
                  value={awaitingNotification}
                  onChange={setAwaitingNotification}
                />
                &nbsp; {I18n.t("awaiting_notification")}
              </label>
            )}
            {!clientView && (
              <label style={{ marginRight: 20 }}>
                <Checkbox
                  checked={allSampleResultsPresent}
                  onChange={(e) => setAllSampleResultsPresent(e.target.checked)}
                />
                &nbsp;&nbsp;
                {I18n.t("all_sample_results_present")}
              </label>
            )}
            {!clientView && (
              <label style={{ marginRight: 20 }}>
                <Checkbox
                  checked={exceedAnalysisTime}
                  onChange={(e) => setExceedAnalysisTime(e.target.checked)}
                />
                &nbsp;&nbsp;
                {I18n.t("exceed_analysis_time")}
              </label>
            )}

            {!clientView && (
              <label style={{ marginRight: 20, color: "green" }}>
                <Checkbox
                  checked={analysis_started_more_than_24_hrs_since_time_sampled}
                  onChange={(e) =>
                    set_analysis_started_more_than_24_hrs_since_time_sampled(e.target.checked)
                  }
                />
                &nbsp; {I18n.t("analysis_started_more_than_24h_since_time_sampled")}
              </label>
            )}
            {!clientView && (
              <label style={{ marginRight: 0, color: "blue" }}>
                <Checkbox
                  checked={analysis_started_less_than_24_hrs_since_time_sampled}
                  onChange={(e) =>
                    set_analysis_started_less_than_24_hrs_since_time_sampled(e.target.checked)
                  }
                />
                &nbsp; {I18n.t("analysis_started_less_than_24h_since_time_sampled")}
              </label>
            )}
          </Column>
        </Row>
        {clientCompanyId && (
          <Row style={{ paddingBottom: 0, paddingTop: 5 }}>
            <FullColumn>
              <DataFieldFilter
                ref={dataFieldFilter}
                onSubmit={() => {}}
                company_id={clientCompanyId}
                company_branch_id={clientBranchId}
              />
            </FullColumn>
          </Row>
        )}
        <Row>
          <FullColumn style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button
              title={loading ? I18n.t("please_wait") : I18n.t("find_tests")}
              icon={loading ? "fa-spin fa-spinner" : "fa-search"}
              disabled={loading || !valid()}
              onClick={onClick}
            />
            {!clientView && (
              <SearchTemplateManager
                formKey={formKey}
                getCurrentParams={getSearchParams}
                applyTemplate={applyTemplate}
                isClientView={clientView}
              />
            )}
          </FullColumn>
        </Row>
      </Container>
    </Fragment>
  );
}
